import React, { useState, useEffect } from 'react';
import { X, Car, User, Calendar, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import FirmaCanvas from './FirmaCanvas';
import api from '../services/api';

/**
 * ModalFirma - Modal para captura de firma presencial
 * Muestra datos del vehículo/conductor y permite firmar
 */
function ModalFirma({ asignacion, tipoFirma, onClose, onSuccess }) {
  const [firmaImagen, setFirmaImagen] = useState(null);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [clausulas, setClausulas] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingClausulas, setLoadingClausulas] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const tipoTexto = tipoFirma === 'ENTREGA' ? 'Entrega' : 'Devolución';

  // Cargar cláusulas legales
  useEffect(() => {
    const cargarClausulas = async () => {
      try {
        const response = await api.get(`/firmas/clausulas/${tipoFirma}`);
        setClausulas(response.data.data?.clausulas || '');
      } catch (err) {
        console.error('Error cargando cláusulas:', err);
        setClausulas('El firmante declara haber recibido/devuelto el vehículo indicado y acepta las condiciones de uso establecidas por la empresa.');
      } finally {
        setLoadingClausulas(false);
      }
    };
    cargarClausulas();
  }, [tipoFirma]);

  const handleFirmar = async () => {
    if (!firmaImagen) {
      setError('Por favor, dibuje su firma');
      return;
    }

    if (!aceptaTerminos) {
      setError('Debe aceptar los términos y condiciones');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post(`/firmas/presencial/${asignacion.asignacion_id || asignacion.id}`, {
        tipoFirma,
        firmaImagen,
        aceptaTerminos: true
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al registrar la firma');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Firma Registrada</h2>
          <p className="text-gray-600">
            La firma de {tipoTexto.toLowerCase()} se ha registrado correctamente.
            {asignacion.conductor_email && ' Se ha enviado un email con el documento.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between ${tipoFirma === 'ENTREGA' ? 'bg-green-600' : 'bg-red-600'}`}>
          <h2 className="text-xl font-bold text-white">
            Firma de {tipoTexto} de Vehículo
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-1 rounded"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Datos del vehículo y conductor */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <Car className="w-5 h-5" />
                <span className="font-medium">Vehículo</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{asignacion.matricula}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <User className="w-5 h-5" />
                <span className="font-medium">Conductor</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {asignacion.conductor_nombre_completo || `${asignacion.conductor_nombre} ${asignacion.conductor_apellidos || ''}`}
              </p>
              <p className="text-sm text-gray-600">DNI: {asignacion.conductor_dni}</p>
            </div>
          </div>

          {/* Fecha de inicio */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-blue-700 mb-2">
              <Calendar className="w-5 h-5" />
              <span className="font-medium">Fecha de Asignación</span>
            </div>
            <p className="text-gray-900">
              Desde: {new Date(asignacion.fecha_inicio).toLocaleDateString('es-ES')}
              {asignacion.fecha_fin && (
                <> - Hasta: {new Date(asignacion.fecha_fin).toLocaleDateString('es-ES')}</>
              )}
              {!asignacion.fecha_fin && (
                <span className="text-blue-600 ml-2">(Asignación activa)</span>
              )}
            </p>
          </div>

          {/* Cláusulas legales */}
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Términos y Condiciones</h3>
            {loadingClausulas ? (
              <div className="text-center py-4 text-gray-500">Cargando...</div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg max-h-40 overflow-y-auto text-sm text-gray-700 whitespace-pre-line">
                {clausulas}
              </div>
            )}
          </div>

          {/* Canvas de firma */}
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Su Firma</h3>
            <FirmaCanvas
              onFirmaChange={setFirmaImagen}
              width={500}
              height={200}
            />
          </div>

          {/* Checkbox de aceptación */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={aceptaTerminos}
              onChange={(e) => setAceptaTerminos(e.target.checked)}
              className="w-5 h-5 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              He leído y acepto los términos y condiciones. Confirmo que la información es correcta y que la firma es mía.
            </span>
          </label>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Botones */}
          <div className="flex gap-4 pt-4 border-t">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleFirmar}
              disabled={loading || !firmaImagen || !aceptaTerminos}
              className={`flex-1 px-6 py-3 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 ${
                tipoFirma === 'ENTREGA'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Registrando...
                </>
              ) : (
                <>Firmar {tipoTexto}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalFirma;
